import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export function Page({
  title,
  actions,
  back,
  leading,
  trailing,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  /** a "← parent" link shown above the title (kept out of the heading) */
  back?: ReactNode;
  /** left / right of the title on the same row, outside the heading (the asset page's back arrow and state chip) */
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const heading = (
    <Typography variant="h5" component="h1">
      {title}
    </Typography>
  );
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {back ? <Box sx={{ mb: 1 }}>{back}</Box> : null}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        {leading || trailing ? (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {leading}
            {heading}
            {trailing}
          </Stack>
        ) : (
          heading
        )}
        {actions ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {actions}
          </Stack>
        ) : null}
      </Stack>
      {children}
    </Box>
  );
}

export function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }} role="status">
      <CircularProgress aria-label="loading" />
    </Box>
  );
}

export function LoadError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <Alert
      severity="error"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : undefined
      }
    >
      {t('common.error_generic')}
      {error instanceof Error ? (
        <Box component="span" sx={{ display: 'block', opacity: 0.7, fontSize: 12 }}>
          {error.message}
        </Box>
      ) : null}
    </Alert>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: 'center',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        borderStyle: 'dashed',
      }}
    >
      {children}
    </Box>
  );
}
